import { useState, useCallback, useMemo } from 'react';

import { useCartMutations } from './useCartMutations';
import { dispatchCartRefreshEvent } from '../../lib';

import type { CreateCartData } from '../../lib/apiClient';
import type { CartItemInput, CheckoutContextValue } from '../../types';

/**
 * Composition hook for checkout state management
 * Simple approach: cart ID in state only, no localStorage persistence
 *
 * useMemo stabilizes the returned object reference used as context value.
 * React Compiler handles this automatically, but we keep useMemo
 * as an explicit safety net for clarity.
 */
export function useCheckoutState(): CheckoutContextValue {
  const [cartId, setCartId] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    createGuestCart,
    addToCart,
    isCreatingCart,
  } = useCartMutations({
    onCartCreated: setCartId,
    onAddToCartSuccess: dispatchCartRefreshEvent,
    onError: setErrorMessage,
    setAddingToCart,
  });

  const clearError = useCallback(() => {
    setErrorMessage(null);
  }, []);

  /**
   * Add product to cart
   * Creates cart first if needed
   */
  const handleAddToCart = useCallback(
    (data: CartItemInput) => {
      setErrorMessage(null);
      setAddingToCart(data.sku);

      if (!cartId) {
        // No cart yet - create one first, then add product
        // Note: mutationFn already unwraps response.data, so we receive CreateCartData directly
        createGuestCart(undefined, {
          onSuccess: (response: CreateCartData) => {
            const newCartId = response?.createEmptyCart ?? null;
            setCartId(newCartId);
            if (newCartId) {
              addToCart({ cartId: newCartId, data });
            } else {
              setAddingToCart(null);
              setErrorMessage('Failed to create cart. Please try again.');
            }
          },
        });
      } else {
        // Cart exists - add product directly
        addToCart({ cartId, data });
      }
    },
    [cartId, createGuestCart, addToCart],
  );

  return useMemo(
    () => ({
      handleAddToCart,
      clearError,
      addingToCart,
      errorMessage,
      isInitializing: isCreatingCart,
    }),
    [handleAddToCart, clearError, addingToCart, errorMessage, isCreatingCart]
  );
}
